import PublicNav from '../components/PublicNav';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import PaywallReturnLink from '../components/PaywallReturnLink';

const EFFECTIVE_DATE = 'October 5, 2026';

export default function Privacy() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', display: 'flex', flexDirection: 'column' }}>
      <PageMeta title="Privacy Policy" description="Read the Agence Privacy Policy — how we collect, use, and protect your financial data when you use the AI-powered finance and investment copilot." path="/privacy" />
      <PublicNav />
      <main style={{ flex: 1, maxWidth: 720, margin: '0 auto', padding: '2rem 1.5rem 4rem', width: '100%' }}>
        <PaywallReturnLink />
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '2.5rem' }}>
          Effective {EFFECTIVE_DATE}
        </p>

        <Section title="Overview">
          Agence is a personal finance and investment copilot. To provide its service, Agence
          connects to your bank accounts and investment accounts, analyzes your financial data
          using AI agents, and surfaces prioritized insights. This policy explains what data we
          collect, how we use it, how we protect it, and your rights regarding that data.
        </Section>

        <Section title="Information We Collect">
          <strong>Information you provide directly:</strong>
          <ul style={listStyle}>
            <li><strong>Account credentials</strong> — your email address and password. Passwords are hashed with bcrypt and never stored in plain text. If you sign in with Google or Apple instead, we receive your email and name from that provider and store an opaque provider-issued identifier to recognize your account on return visits — no password is created or stored in that case.</li>
            <li><strong>Goals and preferences</strong> — savings goals, budget limits, notification settings, household sharing preferences, and other configurations you set within the app.</li>
            <li><strong>Card credits</strong> — for credit cards with recurring credits Agence tracks, we work out which credits were used from that card's transactions, and store any credit you mark as used yourself, for the current period. We use them only to show your credits and remind you before one resets unused. Deleted with the card or your Agence account.</li>
            <li><strong>Rebalance reminders</strong> — if you set a rebalance reminder on an investment account (a brokerage account you connected, or one linked through Plaid), we store the interval and the date you last rebalanced, plus a copy of that account's positions at that moment (each ticker or fund name, quantity, market value, and its share of the account). We use the copy only to show you what has changed when the reminder comes due. It is replaced each time you mark the account rebalanced, and deleted when you remove the reminder, unlink the account, or delete your Agence account.</li>
            <li><strong>AI chat</strong> — if you use the AI chat, we store your messages and its replies, and any rating or feedback you give on a reply. Chat also keeps a short memory of facts from your conversations (for example a goal you mentioned) to personalize later replies. The lookups chat runs to answer you (for example reading your portfolio) are kept for 72 hours so we can show which data an answer used, then deleted.</li>
            <li><strong>Automations and orders</strong> — the rules you configure for automations, the result of each automation run, the orders placed through Agence (ticker, side, quantity or amount, price, and order type), and the date you gave consent for live trading, if you did.</li>
            <li><strong>Corrections and reports</strong> — transaction categories you change, merchant rules you set, and transactions you report to us as wrong.</li>
            <li><strong>Households</strong> — if you create or join a household, its members' email addresses. Goals and budgets marked as shared are visible to every member, including progress and contributions. A shared budget shows the household's combined spending in its category, so it includes your transactions in that category. Anything not marked as shared stays private to you.</li>
          </ul>
          <br />
          <strong>Information retrieved from connected services:</strong>
          <ul style={listStyle}>
            <li><strong>Bank account data (via Plaid)</strong> — transaction history, account balances (including one saved reading per account per day, so a goal's progress can be measured from an earlier balance; deleted when you unlink the account or delete your Agence account), account metadata (institution name, account type, the account's product name such as "Platinum Card", last four digits of account number), and recurring transaction streams. Retrieved only from institutions you explicitly connect.</li>
            <li><strong>Investment account data (via your connected brokerage)</strong> — portfolio positions, holdings, trade history, equity history, and cash balance, retrieved from the brokerage account you link, either through the brokerage's sign-in (OAuth) or with API keys you provide. Your brokerage is a separate, independent service from Agence. For a brokerage account an automation runs on, we keep a copy of its trade records (fills, trading fees, and position changes such as splits) to work out each automation&apos;s results and costs; deposits, withdrawals, and other cash records are not kept.</li>
            <li><strong>Investment holdings (via Plaid Investments)</strong> — if you connect a brokerage through Plaid, we retrieve holdings, securities, and cost basis data.</li>
            <li><strong>News and company data (via Finnhub)</strong> — news articles and company information for tickers on your watchlist and tickers you ask about. Only the ticker symbol is sent. This data is not personally identifiable. Market prices come from your brokerage provider's market data service.</li>
            <li><strong>Fund and company data (via Alpha Vantage)</strong> — fund fees and size, analyst price targets and earnings estimates, dividend and split history, and institutional ownership for tickers you ask about. Only the ticker symbol is sent. This data is not personally identifiable.</li>
          </ul>
          <br />
          <strong>Payment data:</strong>
          <ul style={listStyle}>
            <li><strong>Billing information</strong> — on the web, Plus is paid through Stripe. In our iOS and Android apps, it is paid through Apple's App Store or Google Play, and RevenueCat manages those subscriptions for us. Agence never receives or stores your card number, expiry, or CVV. We keep a Stripe customer ID or your app-store subscription status, and your plan, to manage your account.</li>
          </ul>
          <br />
          <strong>Technical data:</strong>
          <ul style={listStyle}>
            <li><strong>Server logs</strong> — standard request logs (timestamp, endpoint, response code, and your account ID) retained for debugging and security monitoring. No behavioral tracking or advertising analytics.</li>
            <li><strong>Security data</strong> — if you turn on two-factor authentication, its secret key (encrypted) and your backup codes (stored only as one-way hashes); and your signed-in sessions, also stored only as hashes.</li>
            <li><strong>Notifications and usage</strong> — the in-app notifications we've sent you, and how many AI chat messages and insight refreshes you used each day, so your plan's limits apply.</li>
            <li><strong>Service health records</strong> — when an automation, sync, or order check fails, an internal record linking the problem to the affected account, so we can fix it. Deleted with your Agence account.</li>
            <li><strong>Push notification token</strong> — if you enable push notifications on our mobile app, we store a device token used to deliver notifications about events you've enabled in Settings (budget alerts, trade signals, goal completion, and similar). You can disable this at any time from Settings or your device's system settings.</li>
            <li><strong>Biometric sign-in devices</strong> — if you turn on Face ID or fingerprint sign-in on our mobile app, we store a record for that phone: a random device identifier, the platform (iOS or Android), a label such as "iPhone", when it was added and last used, and a one-way hash of a sign-in key. The key itself stays in your phone's secure storage, which releases it only after your phone confirms your face or fingerprint. We never receive your face or fingerprint data, only the sign-in key once your phone has checked it. The record expires after 90 days without use, and you can remove any device at any time from Settings.</li>
          </ul>
        </Section>

        <Section title="How We Use Your Data">
          <ul style={listStyle}>
            <li>Running AI analysis agents to generate financial insights tailored to your accounts.</li>
            <li>Synthesizing agent outputs via Claude (Anthropic) to produce a ranked insight feed and chat responses.</li>
            <li>Tracking progress toward your savings goals and budget limits.</li>
            <li>Executing automated paper (simulated) trades based on autopilot rules you configure, and surfacing trade signals for self-directed live trading at your discretion.</li>
            <li>Detecting recurring charges and upcoming subscription renewals.</li>
            <li>Processing subscription payments via Stripe.</li>
            <li>Administering accounts and providing support: Agence can see each account&apos;s email, plan, sign-up date, last sign-in and which kinds of accounts are linked, to keep the service running and answer questions.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            We do not sell your data. We do not share your data with third parties for advertising
            or marketing purposes. Data is shared only with the service providers listed below,
            solely to operate the product.
          </p>
        </Section>

        <Section title="AI Processing of Your Financial Data">
          Agence uses Anthropic's Claude API to synthesize AI agent outputs and power the AI
          chat feature. <strong>Financial data — including transaction summaries, portfolio
          positions, goals, and budget information — is transmitted to Anthropic's API as
          part of this processing.</strong>
          <p style={{ marginTop: '0.75rem' }}>
            <strong>We ask your permission before any of this is sent.</strong> AI analysis is
            off until you explicitly allow it — Agence names the data involved and identifies
            Anthropic as the recipient at that point, and you can withdraw permission at any
            time under Account → AI analysis. If you decline or later turn it off, Agence
            keeps working without AI: account balances, spending, budgets, goals and portfolio
            tracking are all unaffected, and nothing is sent to Anthropic.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            <strong>What is sent, specifically:</strong> bank transactions, balances and
            spending categories; investment holdings, positions and portfolio values; savings
            goals and budgets; tickers on your watchlist and symbols you view; and the messages
            you send to the AI chat. Your bank and brokerage credentials are never sent — they
            never reach Agence in the first place (see Information We Collect, above).
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            Per Anthropic's usage policies, data submitted through the API is not used to train
            Anthropic's models. Anthropic processes this data solely to generate the requested
            output and does not retain it beyond what is necessary for that generation.{' '}
            <strong>
              On that basis we consider Anthropic&apos;s handling of this data to provide
              protection equivalent to our own commitments in this policy: it is used only to
              produce the output you requested, not for training, not for advertising, and not
              sold or shared onward.
            </strong>{' '}
            See{' '}
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Anthropic's Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Commercial Terms
            </a>{' '}
            for the full terms.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            AI-generated insights, signals, and chat responses may contain errors or omissions.
            They are provided for informational purposes only and do not constitute financial,
            investment, or legal advice. See our{' '}
            <Link to="/terms" style={linkStyle}>Terms of Service</Link> for the full disclaimer.
          </p>
        </Section>

        <Section title="Third-Party Service Providers">
          Agence relies on the following third-party services to operate. Each processes data
          only as necessary to provide their specific function:
          <ul style={listStyle}>
            <li>
              <strong>Plaid</strong> — connects to your financial institutions to retrieve bank
              account data and investment holdings. When you link an account, you authenticate
              directly with your institution through Plaid's interface — Agence never sees your
              banking username or password. Subject to{' '}
              <a href="https://plaid.com/legal/#privacy-policy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Plaid's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Supported brokerage provider</strong> — portfolio data and trade execution
              via OAuth. Your connected brokerage is an independent, separate entity from Agence;
              please review your brokerage's privacy policy and disclosures when connecting
              your account.
            </li>
            <li>
              <strong>Finnhub</strong> — news and company information for tickers. Receives ticker symbols only, never personal or account data.
            </li>
            <li>
              <strong>Alpha Vantage</strong> — fund data, analyst targets and estimates, dividends,
              splits, and institutional ownership. Receives ticker symbols only, never personal or account data.
            </li>
            <li>
              <strong>Anthropic</strong> — AI insight synthesis and chat responses. Financial
              data is sent to Anthropic's API to generate insights. API data is not used to
              train Anthropic's models. Subject to{' '}
              <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Anthropic's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Stripe</strong> — subscription billing and payment processing for Plus
              plans on the web. Stripe handles all payment card data directly; Agence does not store card
              numbers or CVV codes. Subject to{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Stripe's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Apple App Store and Google Play</strong> — process Plus subscriptions bought in
              our iOS and Android apps, under their own privacy policies.
            </li>
            <li>
              <strong>RevenueCat</strong> — manages app-store subscriptions for us. It receives your
              Agence account ID and your subscription's purchase status from Apple or Google, never
              your financial data. Subject to{' '}
              <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                RevenueCat's Privacy Policy
              </a>.
            </li>
            <li>
              <strong>SendGrid and Resend</strong> — deliver our emails (password resets, security alerts and
              similar). They receive your email address and the message.
            </li>
            <li>
              <strong>Google and Apple sign-in</strong> — if you sign in with Google or Apple, we verify the
              sign-in with that provider.
            </li>
            <li>
              <strong>Public data sources</strong> — FRED (Federal Reserve economic data), SEC EDGAR filings,
              FINRA data and Cointelegraph news. These receive no personal or account data.
            </li>
            <li>
              <strong>Render</strong> — backend server hosting (API and background workers) and PostgreSQL
              database hosting. All data at rest is stored in Render's managed database.
            </li>
            <li>
              <strong>Vercel</strong> — frontend hosting and delivery.
            </li>
            <li>
              <strong>Firebase Cloud Messaging (Google)</strong> — if you enable push
              notifications on our mobile app, your device token and each notification's
              title, body, category, and the details needed to open it are sent to Google's
              Firebase Cloud Messaging service solely to deliver it. A notification can include
              financial details it is about, such as an unusual charge's merchant and amount.
              Google does not receive your account credentials or chat content through this
              integration. Subject to{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                Firebase's Privacy Policy
              </a>.
            </li>
          </ul>
        </Section>

        <Section title="Data Storage and Security">
          <ul style={listStyle}>
            <li>All data is transmitted over HTTPS/TLS.</li>
            <li>Passwords are hashed with bcrypt (never stored in plain text).</li>
            <li>Plaid access tokens, brokerage OAuth tokens and brokerage API keys are encrypted at rest using AES-256-GCM before storage.</li>
            <li>JSON Web Tokens (JWT) are signed with a server-side secret and expire after a fixed interval.</li>
            <li>Authentication endpoints are rate-limited to prevent brute-force attacks.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            No security measure is perfect. In the event of a breach affecting your personal
            data, we will notify affected users as required by applicable law — within 30 days
            under the Florida Information Protection Act (FIPA), and in the most expedient time
            practicable under California law and other applicable state breach notification
            statutes.
          </p>
        </Section>

        <Section title="Data Retention">
          Your data is retained for as long as your account is active. You may:
          <ul style={listStyle}>
            <li><strong>Unlink a bank account</strong> — go to Settings → find the account → Unlink. Removes that account and all associated transactions from our database immediately. The corresponding Plaid item is also removed, ending data access at the source.</li>
            <li><strong>Unlink a brokerage account</strong> — go to Settings → find the account → Unlink. Removes brokerage OAuth credentials and portfolio data from our database.</li>
            <li><strong>Delete your Agence account</strong> — go to Settings → scroll to the bottom → Delete Account. This immediately and permanently removes all personal data from our systems, including transaction history, portfolio snapshots, goals, chat history, and linked account credentials, and cannot be undone. Note: Stripe retains billing records as required by applicable financial regulations, independent of account deletion.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            Server logs and anonymized analytics may be retained for up to 90 days after the
            events they record.
          </p>
        </Section>

        <Section title="Your Privacy Rights">
          Depending on where you live, you may have the following rights regarding your personal
          data:
          <ul style={listStyle}>
            <li><strong>Right to Know</strong> — request a copy of the personal data we hold about you and information about how we collect, use, and share it.</li>
            <li><strong>Right to Delete</strong> — request deletion of your personal data (subject to limited exceptions required by law or for legitimate business purposes).</li>
            <li><strong>Right to Correct</strong> — request correction of inaccurate personal data we hold about you.</li>
            <li><strong>Right to Opt-Out of Sale or Sharing</strong> — <strong>Agence does not sell or share your personal data for cross-context behavioral advertising.</strong> No opt-out is required, but you may contact us to confirm this at any time.</li>
            <li><strong>Right to Non-Discrimination</strong> — we will not discriminate against you for exercising any of these rights.</li>
          </ul>
          <p style={{ marginTop: '0.75rem' }}>
            <strong>California residents (CCPA/CPRA):</strong> Your financial account data,
            transaction history, and precise portfolio information constitute "sensitive personal
            information" under the CPRA. We use this data only to provide the service and do not
            use it to infer characteristics for profiling or advertising. You may direct us to
            limit use of sensitive personal information to service delivery purposes by contacting
            us at the address below.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:support@agence.finance" style={linkStyle}>support@agence.finance</a>.
            We will respond within 45 days (extendable by 45 days with notice if reasonably
            necessary).
          </p>
        </Section>

        <Section title="Children's Privacy">
          Agence is not directed to children under 13 and does not knowingly collect personal
          information from children under 13 (COPPA). If you believe we have inadvertently
          collected such information, contact us and we will delete it promptly.
        </Section>

        <Section title="Changes to This Policy">
          We may update this policy as the product evolves. Material changes will be communicated
          via in-app notification or email. The effective date at the top of this page reflects
          the most recent update. Continued use of Agence after changes constitutes acceptance
          of the updated policy.
        </Section>

        <Section title="Contact">
          Questions about this policy, data requests, or exercising your privacy rights:{' '}
          <a href="mailto:support@agence.finance" style={linkStyle}>support@agence.finance</a>
          <br /><br />
          See also our{' '}
          <Link to="/terms" style={linkStyle}>Terms of Service</Link>.
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{
        fontSize: '1.05rem',
        fontWeight: 600,
        color: 'var(--text-primary)',
        marginBottom: '0.6rem',
        paddingBottom: '0.4rem',
        borderBottom: '1px solid var(--navy-200)',
      }}>
        {title}
      </h2>
      <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
        {children}
      </div>
    </section>
  );
}

const listStyle = {
  margin: '0.25rem 0 0 1.25rem',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
};

const linkStyle = {
  color: 'var(--navy-600)',
  textDecoration: 'underline',
};
